import { useEffect, useState } from "react";
import { Form, Input, Select, notification } from "antd";

import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { Button, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { useGetAssessmentTemplatesHook } from "@/modules/admin/scenes/hooks/useGetAssessmentTemplates.hook";
import { InterviewGuideHttpService } from "@/modules/assessments/services/interview-guide-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { FORM_VALIDATION_ERROR_MESSAGE } from "@/shared/constants/form.constants";

import { useGetInterviewGuideHook } from "./hooks/useGetInterviewGuide.hook";
import { useInterviewQuestionBankHook } from "./hooks/useInterviewQuestionBank.hook";
import { AddInterviewQuestionModal } from "./AddInterviewQuestionModal";
import style from "./style.module.css";

const QUESTION_SLOT_COUNT = 2;
const INDICATOR_SLOT_COUNT = 3;

interface ICompositeFormValues {
  interviewQuestionIds: (number | undefined)[];
  positiveIndicators: (string | undefined)[];
  negativeIndicators: (string | undefined)[];
}

type FormValues = Record<number, ICompositeFormValues>;

function padSlots<T>(values: T[], slotCount: number): (T | undefined)[] {
  return Array.from({ length: slotCount }, (_, index) => values[index]);
}

function compactQuestionIds(values: (number | undefined)[]): number[] {
  return values.filter((value): value is number => value !== undefined);
}

function compactIndicators(values: (string | undefined)[]): string[] {
  return values.map((value) => value?.trim()).filter((value): value is string => !!value);
}

export default function AssessmentTemplateInterviewGuide() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const { templates, isLoading: isLoadingTemplates } = useGetAssessmentTemplatesHook();
  const { guide, isLoading: isLoadingGuide } = useGetInterviewGuideHook(selectedTemplateId);
  const { questions, isLoading: isLoadingQuestions, addQuestion } = useInterviewQuestionBankHook();

  useEffect(() => {
    if (!guide) {
      form.resetFields();
      return;
    }

    const initialValues: FormValues = {};
    for (const composite of guide.composites) {
      initialValues[composite.compositeId] = {
        interviewQuestionIds: padSlots(composite.interviewQuestionIds, QUESTION_SLOT_COUNT),
        positiveIndicators: padSlots(composite.positiveIndicators, INDICATOR_SLOT_COUNT),
        negativeIndicators: padSlots(composite.negativeIndicators, INDICATOR_SLOT_COUNT),
      };
    }
    form.setFieldsValue(initialValues);
  }, [guide, form]);

  const questionOptions = questions.map((question) => ({ value: question.interviewQuestionId, label: question.body }));

  async function handleSave(values: FormValues) {
    if (!guide || selectedTemplateId === null) return;

    const composites = guide.composites.map((composite) => {
      const fieldValues = values[composite.compositeId] ?? {
        interviewQuestionIds: [],
        positiveIndicators: [],
        negativeIndicators: [],
      };
      return {
        compositeId: composite.compositeId,
        interviewQuestionIds: compactQuestionIds(fieldValues.interviewQuestionIds),
        positiveIndicators: compactIndicators(fieldValues.positiveIndicators),
        negativeIndicators: compactIndicators(fieldValues.negativeIndicators),
      };
    });

    try {
      setIsSaving(true);
      await InterviewGuideHttpService.saveInterviewGuide(selectedTemplateId, composites);
      notification.success({ message: "Interview guide saved successfully." });
    } catch (error) {
      const message = getFriendlyErrorMessage(error) ?? "Failed to save the interview guide.";
      notification.error({ message });
    } finally {
      setIsSaving(false);
    }
  }

  function handleSaveFailed() {
    notification.error({ message: FORM_VALIDATION_ERROR_MESSAGE });
  }

  return (
    <div className="main-container">
      <SEO titleParams={["Assessment Templates Interview Guide"]} />
      <div className="inner-container">
        <h1 className={style.pageTitle}>Assessment Templates Interview Guide</h1>

        <div className={style.card}>
          <label htmlFor="template-select" className={style.fieldLabel}>
            Select the template
          </label>
          <Select
            id="template-select"
            className={style.templateSelect}
            placeholder="Select Template"
            loading={isLoadingTemplates}
            disabled={isLoadingGuide}
            value={selectedTemplateId ?? undefined}
            onChange={(value: number) => setSelectedTemplateId(value)}
            options={templates.map((template) => ({
              value: template.templateId,
              label: template.clientName ? `${template.name} (${template.clientName})` : template.name,
            }))}
          />
        </div>

        {isLoadingGuide && <Loading size={LoadingSize.default} />}

        {!isLoadingGuide && guide && (
          <Form
            className={style.card}
            form={form}
            onFinish={handleSave}
            onFinishFailed={handleSaveFailed}
            scrollToFirstError={{ block: "center" }}
          >
            <div className={style.compositesHeader}>
              <h2>Competencies</h2>
              <Button buttonSize={ButtonSize.small} onClick={() => setIsAddQuestionModalOpen(true)}>
                Add New Interview Question
              </Button>
            </div>

            {guide.composites.map((composite) => (
              <section className={style.compositeSection} key={composite.compositeId}>
                <h3 className={style.compositeTitle}>{composite.title}</h3>

                <h4 className={style.subheading}>Interview Questions</h4>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    prev[composite.compositeId]?.interviewQuestionIds !==
                    cur[composite.compositeId]?.interviewQuestionIds
                  }
                >
                  {({ getFieldValue }) =>
                    Array.from({ length: QUESTION_SLOT_COUNT }, (_, slotIndex) => {
                      const otherSelectedIds = Array.from({ length: QUESTION_SLOT_COUNT }, (_, index) => index)
                        .filter((index) => index !== slotIndex)
                        .map((index) => getFieldValue([composite.compositeId, "interviewQuestionIds", index]));
                      const options = questionOptions.filter((option) => !otherSelectedIds.includes(option.value));

                      return (
                        <Form.Item
                          key={slotIndex}
                          name={[composite.compositeId, "interviewQuestionIds", slotIndex]}
                          rules={
                            slotIndex === 0
                              ? [{ required: true, message: "Please select an interview question." }]
                              : undefined
                          }
                        >
                          <Select
                            allowClear
                            showSearch
                            loading={isLoadingQuestions}
                            placeholder="Select Question"
                            options={options}
                            optionFilterProp="label"
                            aria-label={`${composite.title} — interview question ${slotIndex + 1}`}
                          />
                        </Form.Item>
                      );
                    })
                  }
                </Form.Item>

                <div className={style.indicatorColumns}>
                  <div className={style.indicatorColumn}>
                    <h4 className={style.subheading}>Positive Indicator (Write up to {INDICATOR_SLOT_COUNT})</h4>
                    {Array.from({ length: INDICATOR_SLOT_COUNT }, (_, slotIndex) => (
                      <Form.Item
                        key={slotIndex}
                        name={[composite.compositeId, "positiveIndicators", slotIndex]}
                        rules={
                          slotIndex === 0
                            ? [{ required: true, whitespace: true, message: "Please write a positive indicator." }]
                            : undefined
                        }
                      >
                        <Input
                          placeholder="Write a Positive Indicator"
                          aria-label={`${composite.title} — positive indicator ${slotIndex + 1}`}
                        />
                      </Form.Item>
                    ))}
                  </div>
                  <div className={style.indicatorColumn}>
                    <h4 className={style.subheading}>Negative Indicator (Write up to {INDICATOR_SLOT_COUNT})</h4>
                    {Array.from({ length: INDICATOR_SLOT_COUNT }, (_, slotIndex) => (
                      <Form.Item
                        key={slotIndex}
                        name={[composite.compositeId, "negativeIndicators", slotIndex]}
                        rules={
                          slotIndex === 0
                            ? [{ required: true, whitespace: true, message: "Please write a negative indicator." }]
                            : undefined
                        }
                      >
                        <Input
                          placeholder="Write a Negative Indicator"
                          aria-label={`${composite.title} — negative indicator ${slotIndex + 1}`}
                        />
                      </Form.Item>
                    ))}
                  </div>
                </div>
              </section>
            ))}

            <div className={style.saveButtonContainer}>
              <SubmitButton buttonSize={ButtonSize.small} isLoading={isSaving}>
                Save
              </SubmitButton>
            </div>
          </Form>
        )}
      </div>

      <AddInterviewQuestionModal
        isOpen={isAddQuestionModalOpen}
        onClose={() => setIsAddQuestionModalOpen(false)}
        addQuestion={addQuestion}
      />
    </div>
  );
}
